import React from "react";
import RoleProficiencyThresholdsDisplay from "./RoleProficiencyThresholdsDisplay";
import { getRightworkAPI } from "../requests/utilities/requests";
import { postRightworkAPI } from "../requests/utilities/requests";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadSpinner from "../../../utilities/LoadSpinner";
import {
  API_get_learning_modules_by_role,
  API_post_learning_module_star_threshold,
  API_delete_learning_module_star_threshold,
} from "../../../constants";
import WorkIcon from "@mui/icons-material/Work";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

// Configuration page (Configurations > Training Proficiency). Moved out of the
// Training Overview tab, which is now analytics-only. Lets admins/managers set
// which modules earn each proficiency star rating per role.
function TrainingProficiencyDisplay(props) {
  const [learningModulesByRole, setLearningModulesByRole] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);

  const homeCompany = props.storeNames?.filter(
    (s) => s.id === props.homeStore
  )[0]?.["company"];

  // [GET] api/lms_info/get_learning_modules_by_role
  // * company_id
  const fetchLearningModulesByRole = async (showLoading) => {
    setIsLoading(showLoading);
    try {
      const res = await getRightworkAPI(API_get_learning_modules_by_role, {
        company_id: homeCompany,
      });
      if (res.status === 200) {
        setLearningModulesByRole(res.data.redux?.payload?.roles);
        setIsLoading(false);
      }
    } catch (error) {
      throw Error(error);
    }
  };

  // [POST] api/lms_info/post_learning_module_star_threshold
  // Assign one or more modules to a star level for a role.
  // * company_id, role_id, star_rating, learning_module_uuids (array)
  const post_learning_module_star_threshold = async (
    role_id,
    star_rating,
    learning_module_uuids
  ) => {
    const requestData = {
      company_id: homeCompany,
      role_id: role_id,
      star_rating: star_rating,
      learning_module_uuids: JSON.stringify(learning_module_uuids),
    };

    try {
      toast.loading("Saving...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_learning_module_star_threshold,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Saved", { autoClose: 2000 });

        fetchLearningModulesByRole(false);
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
    }
  };

  // [POST] api/lms_info/delete_learning_module_star_threshold
  // Remove a single module from a role's ladder.
  // * company_id, role_id, learning_module_uuid
  const delete_learning_module_star_threshold = async (
    role_id,
    learning_module_uuid
  ) => {
    const requestData = {
      company_id: homeCompany,
      role_id: role_id,
      learning_module_uuid: learning_module_uuid,
    };

    try {
      toast.loading("Removing...", { autoClose: false });

      const res = await postRightworkAPI(
        API_delete_learning_module_star_threshold,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Removed", { autoClose: 2000 });

        fetchLearningModulesByRole(false);
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
    }
  };

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await fetchLearningModulesByRole(true);
      } finally {
        if (!cancelled) setInitialLoadComplete(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {initialLoadComplete && !isLoading ? (
        <div className="min-h-screen bg-white">
          {/* Header Section */}
          <div className="bg-white border-b border-gray-200">
            <div className="px-6 py-8">
              <div className="mb-4">
                <h1 className="text-2xl font-bold text-slate-900 mb-1">
                  Training Proficiency
                </h1>
                <p className="text-sm text-slate-600">
                  Set the modules required to reach each proficiency level per role
                </p>
              </div>

              {/* Stats Row */}
              <div className="flex items-center gap-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <WorkIcon style={{ fontSize: "18px" }} />
                  <span>{learningModulesByRole.length} Roles</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUpIcon style={{ fontSize: "18px" }} />
                  <span>Proficiency Thresholds</span>
                </div>
              </div>
            </div>
          </div>

          <RoleProficiencyThresholdsDisplay
            learningModulesByRole={learningModulesByRole}
            homeCompany={homeCompany}
            post_learning_module_star_threshold={
              post_learning_module_star_threshold
            }
            delete_learning_module_star_threshold={
              delete_learning_module_star_threshold
            }
            proficiencyStarsCount={props.proficiencyStarsCount}
          />
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadSpinner />
        </div>
      )}
    </>
  );
}

export default TrainingProficiencyDisplay;
